import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import { Canvas, ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera, useGLTF } from '@react-three/drei';
import { Loader2, RotateCcw, RotateCw, Scaling } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { getProductModelObjectUrl } from '../../edit-product-v3/api/productV2Api';
import { prepareRoomScene, RawRoomScene, RoomScene, snapToFloor } from './room-scene';

/**
 * Place in a room — pose a product's GLB on the floor of an analysed room.
 *
 * The visualizer's furniture placement, on a room generated in Content Studio.
 * The room was analysed into a Space when it was chosen, so this works from
 * the same scene the visualizer uses: the camera at the origin with the room's
 * own field of view, the floor's plane equation in metres, and the room's depth
 * as an invisible mesh — which is what puts a rug UNDER the coffee table rather
 * than painted over it.
 *
 * Validating renders the product (and the invisible depth) once into the
 * transparent canvas. Laid over the room that frame is the composite; its
 * alpha is the product's visible footprint.
 */

export interface RoomPlacement {
    /** Point on the floor, metres, camera space. */
    x: number;
    y: number;
    z: number;
    /** Turn about the floor normal, degrees. */
    rotDeg: number;
    /** Multiplier on the product's true size. */
    scale: number;
}

export interface PlaceInRoomResult {
    compositeUrl: string;
    rugMaskUrl?: string;
    placement: RoomPlacement;
}

interface PlaceInRoomDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    spaceId: string;
    loadScene: (spaceId: string) => Promise<RawRoomScene>;
    productUuid?: string;
    modelPath?: string;
    isRug: boolean;
    /** The product's largest real dimension in metres; the GLB is scaled to it. */
    largestDimensionM?: number;
    placement?: RoomPlacement;
    onUpload: (file: File) => Promise<string>;
    onValidated: (result: PlaceInRoomResult) => void;
}

const HELPER = 'placeInRoomHelper';
const UP = new THREE.Vector3(0, 1, 0);

/** The GLB, laid flat if it is a rug, at its true size, base on the floor, standing on the floor's normal. */
const PlacedModel: React.FC<{
    url: string;
    isRug: boolean;
    largestDimensionM?: number;
    placement: RoomPlacement;
    floorNormal: THREE.Vector3;
    onGrab: (e: ThreeEvent<PointerEvent>) => void;
    groupRef?: React.MutableRefObject<THREE.Group | null>;
}> = ({ url, isRug, largestDimensionM, placement, floorNormal, onGrab, groupRef }) => {
    const { scene } = useGLTF(url) as any;
    const model = useMemo(() => {
        const c = scene.clone(true) as THREE.Group;
        // Rug GLBs are often authored standing up, the pattern on X/Y. Lay the
        // thinnest axis vertical so the pattern faces the ceiling.
        if (isRug) {
            const s0 = new THREE.Box3().setFromObject(c).getSize(new THREE.Vector3());
            const thin = Math.min(s0.x, s0.y, s0.z);
            if (thin === s0.z) c.rotateX(-Math.PI / 2);
            else if (thin === s0.x) c.rotateZ(Math.PI / 2);
            c.updateMatrixWorld(true);
        }
        // True size when the product says it; otherwise the GLB's own metres,
        // which is what the visualizer assumes of every model.
        const size = new THREE.Box3().setFromObject(c).getSize(new THREE.Vector3());
        const authored = Math.max(size.x, size.y, size.z) || 1;
        if (largestDimensionM && largestDimensionM > 0.05) c.scale.multiplyScalar(largestDimensionM / authored);
        c.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(c);
        const center = box.getCenter(new THREE.Vector3());
        c.position.x -= center.x;
        c.position.z -= center.z;
        // Base on the floor; a rug a hair above it, so nothing z-fights.
        c.position.y -= box.min.y - (isRug ? 0.004 : 0);
        return c;
    }, [scene, isRug, largestDimensionM]);

    // Stand on the floor's normal, then turn about it — FurnitureItem's order.
    const quaternion = useMemo(() => {
        const align = new THREE.Quaternion().setFromUnitVectors(UP, floorNormal);
        const yaw = new THREE.Quaternion().setFromAxisAngle(UP, THREE.MathUtils.degToRad(placement.rotDeg));
        return align.multiply(yaw);
    }, [floorNormal, placement.rotDeg]);

    return (
        <group
            ref={groupRef}
            position={[placement.x, placement.y, placement.z]}
            quaternion={quaternion}
            scale={placement.scale}
            onPointerDown={onGrab}
        >
            <primitive object={model} />
        </group>
    );
};

/**
 * Keeps the controls above the product — SelectedItemTracker, from the
 * visualizer: project the top-centre of the product's box to the screen every
 * frame and write the position straight onto the element, no React render.
 */
const ControlsTracker: React.FC<{
    target: React.MutableRefObject<THREE.Group | null>;
    overlay: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ target, overlay }) => {
    const camera = useThree((s) => s.camera);
    const size = useThree((s) => s.size);
    const box = useMemo(() => new THREE.Box3(), []);
    const top = useMemo(() => new THREE.Vector3(), []);
    useFrame(() => {
        const g = target.current;
        const el = overlay.current;
        if (!g || !el) return;
        box.setFromObject(g);
        if (box.isEmpty()) return;
        top.set((box.min.x + box.max.x) / 2, box.max.y, (box.min.z + box.max.z) / 2).project(camera);
        const x = Math.min(size.width - 60, Math.max(60, ((top.x + 1) / 2) * size.width));
        const y = Math.min(size.height - 8, Math.max(56, ((1 - top.y) / 2) * size.height));
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.style.opacity = '1';
    });
    return null;
};

/**
 * A knob you drag sideways — DragKnob, from the visualizer. The value follows
 * the horizontal distance from where the drag began, so it is smooth at any
 * speed; a double-click puts it back.
 */
const DragKnob: React.FC<{
    value: number;
    sensitivity: number;
    onChange: (v: number) => void;
    onReset: () => void;
    label: string;
    children: React.ReactNode;
}> = ({ value, sensitivity, onChange, onReset, label, children }) => {
    const start = useRef<{ x: number; v: number } | null>(null);
    const [active, setActive] = useState(false);
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            onPointerDown={(e) => {
                e.stopPropagation();
                e.currentTarget.setPointerCapture(e.pointerId);
                start.current = { x: e.clientX, v: value };
                setActive(true);
            }}
            onPointerMove={(e) => {
                if (!start.current) return;
                onChange(start.current.v + (e.clientX - start.current.x) * sensitivity);
            }}
            onPointerUp={(e) => {
                e.currentTarget.releasePointerCapture?.(e.pointerId);
                start.current = null;
                setActive(false);
            }}
            onDoubleClick={onReset}
            style={{ touchAction: 'none' }}
            className={cn(
                'flex size-9 cursor-ew-resize items-center justify-center rounded-full border text-foreground backdrop-blur-xl transition-colors',
                active ? 'border-foreground/20 bg-background' : 'border-foreground/10 bg-background/85 hover:bg-background',
            )}
        >
            {children}
        </button>
    );
};

/** Hands the renderer, scene and camera out, so validation can shoot one frame. */
const CaptureBridge: React.FC<{ onReady: (s: { gl: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera }) => void }> = ({ onReady }) => {
    const gl = useThree((s) => s.gl);
    const scene = useThree((s) => s.scene);
    const camera = useThree((s) => s.camera);
    useEffect(() => { onReady({ gl, scene, camera }); }, [gl, scene, camera, onReady]);
    return null;
};

function blobOf(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('The frame could not be read'))),
        'image/png',
    ));
}

export const PlaceInRoomDialog: React.FC<PlaceInRoomDialogProps> = ({
    open,
    onOpenChange,
    spaceId,
    loadScene,
    productUuid,
    modelPath,
    isRug,
    largestDimensionM,
    placement: initialPlacement,
    onUpload,
    onValidated,
}) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    // ── The stage the room is fitted into ────────────────────────────────
    // A callback ref: the dialog mounts its content after `open` flips, so an
    // effect keyed on `open` would find no element and never measure it.
    const [stage, setStage] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    const observerRef = useRef<ResizeObserver | null>(null);
    const stageRef = useCallback((el: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (!el) return;
        const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        observerRef.current = ro;
    }, []);
    useEffect(() => () => observerRef.current?.disconnect(), []);

    // ── The analysed room ────────────────────────────────────────────────
    const [room, setRoom] = useState<RoomScene | null>(null);
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setRoom(null);
        setError(null);
        loadScene(spaceId)
            .then((raw) => prepareRoomScene(raw))
            .then((r) => { if (!cancelled) setRoom(r); })
            .catch((e) => {
                if (!cancelled) setError(e?.message || (t('CONTENT_STUDIO.PLACE.ERROR_SCENE', 'The room could not be loaded.') as string));
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, spaceId]);
    useEffect(() => () => { room?.occluder?.dispose(); }, [room]);

    const fit = useMemo(() => {
        if (!room || !stage.w || !stage.h) return null;
        const s = Math.min(stage.w / room.width, stage.h / room.height);
        return { w: Math.round(room.width * s), h: Math.round(room.height * s) };
    }, [room, stage]);

    // ── The model ────────────────────────────────────────────────────────
    const [modelUrl, setModelUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!open) { setModelUrl(null); return; }
        if (!productUuid) { setModelUrl(modelPath || null); return; }
        let objectUrl: string | null = null;
        let cancelled = false;
        getProductModelObjectUrl(productUuid)
            .then((u) => {
                if (cancelled) { URL.revokeObjectURL(u); return; }
                objectUrl = u;
                setModelUrl(u);
            })
            .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load the 3D model'); });
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [open, productUuid, modelPath]);

    // ── Where the product stands ─────────────────────────────────────────
    // Default: the floor's centroid, snapped onto its plane — where the
    // visualizer spawns a piece.
    const defaultPlacement = useMemo<RoomPlacement | null>(() => {
        if (!room) return null;
        const c = new THREE.Vector3(...room.floor.centroid);
        const p = snapToFloor(c, room.floorPlane) || c;
        return { x: p.x, y: p.y, z: p.z, rotDeg: 0, scale: 1 };
    }, [room]);
    const [placement, setPlacement] = useState<RoomPlacement | null>(
        initialPlacement && typeof (initialPlacement as any).y === 'number' ? initialPlacement : null,
    );
    const current = placement || defaultPlacement;
    const update = useCallback((patch: Partial<RoomPlacement>) => {
        setPlacement((prev) => ({ ...(prev || defaultPlacement!), ...patch }));
    }, [defaultPlacement]);
    const setRotation = (deg: number) => update({ rotDeg: ((deg % 360) + 360) % 360 });
    const setScale = (s: number) => update({ scale: Math.min(2, Math.max(0.3, s)) });
    const modelGroup = useRef<THREE.Group | null>(null);
    const controlsRef = useRef<HTMLDivElement | null>(null);

    // ←/→ turn it, as in the visualizer: 15° a press, 1° with Shift.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (!current || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
            const step = (e.shiftKey ? 1 : 15) * (e.key === 'ArrowLeft' ? -1 : 1);
            e.preventDefault();
            setRotation(current.rotDeg + step);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, current?.rotDeg]);

    // ── Dragging across the floor plane ──────────────────────────────────
    const dragRef = useRef<THREE.Vector3 | null>(null);
    const onGrab = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!room || !current) return;
        e.stopPropagation();
        const hit = e.ray.intersectPlane(room.floorPlane, new THREE.Vector3());
        if (!hit) return;
        dragRef.current = new THREE.Vector3(current.x, current.y, current.z).sub(hit);
        (e.target as any)?.setPointerCapture?.(e.pointerId);
    }, [room, current]);
    const onFloorMove = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!dragRef.current || !room) return;
        const hit = e.ray.intersectPlane(room.floorPlane, new THREE.Vector3());
        if (!hit || hit.z > -0.3) return; // never behind or under the camera
        const p = hit.add(dragRef.current);
        setPlacement((prev) => ({ ...(prev || defaultPlacement!), x: p.x, y: p.y, z: p.z }));
    }, [room, defaultPlacement]);
    const endDrag = useCallback(() => { dragRef.current = null; }, []);

    // The drag target: SurfacePlane — a 40 m plane at the floor's centroid,
    // facing along its normal.
    const floorHelper = useMemo(() => {
        if (!room) return null;
        const pos = new THREE.Vector3(...room.floor.centroid);
        const o = new THREE.Object3D();
        o.position.copy(pos);
        o.lookAt(pos.clone().add(room.floorNormal));
        return { position: o.position.clone(), quaternion: o.quaternion.clone() };
    }, [room]);

    // ── Validation: one frame, two uploads ───────────────────────────────
    const three = useRef<{ gl: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera } | null>(null);
    const onThreeReady = useCallback((s: { gl: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera }) => {
        three.current = s;
    }, []);
    const [saving, setSaving] = useState(false);
    const validate = async () => {
        const ctx3 = three.current;
        if (!ctx3 || !room || !current) return;
        setSaving(true);
        setError(null);
        const hidden: THREE.Object3D[] = [];
        try {
            ctx3.scene.traverse((o) => {
                if (o.userData?.[HELPER] && o.visible) { o.visible = false; hidden.push(o); }
            });
            ctx3.gl.setClearColor(0x000000, 0);
            ctx3.gl.clear();
            ctx3.gl.render(ctx3.scene, ctx3.camera);
            const frame = ctx3.gl.domElement;
            const roomImg = new Image();
            roomImg.src = room.imageUrl;
            await roomImg.decode();

            const composite = document.createElement('canvas');
            composite.width = room.width;
            composite.height = room.height;
            const cc = composite.getContext('2d')!;
            cc.drawImage(roomImg, 0, 0, room.width, room.height);
            cc.drawImage(frame, 0, 0, room.width, room.height);
            const compositeUrl = await onUpload(new File([await blobOf(composite)], `place-in-room-${Date.now()}.png`, { type: 'image/png' }));

            let rugMaskUrl: string | undefined;
            if (isRug) {
                const mask = document.createElement('canvas');
                mask.width = room.width;
                mask.height = room.height;
                const mc = mask.getContext('2d')!;
                mc.drawImage(frame, 0, 0, room.width, room.height);
                const data = mc.getImageData(0, 0, room.width, room.height);
                const d = data.data;
                for (let i = 0; i < d.length; i += 4) {
                    const v = d[i + 3] > 16 ? 255 : 0;
                    d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
                }
                mc.putImageData(data, 0, 0);
                rugMaskUrl = await onUpload(new File([await blobOf(mask)], `place-in-room-mask-${Date.now()}.png`, { type: 'image/png' }));
            }

            onValidated({ compositeUrl, rugMaskUrl, placement: current });
            onOpenChange(false);
        } catch (err: any) {
            setError(err?.message || (t('CONTENT_STUDIO.PLACE.ERROR_SAVE', 'The placement could not be saved.') as string));
        } finally {
            hidden.forEach((o) => { o.visible = true; });
            setSaving(false);
        }
    };

    // The 3D frame is drawn at the room's own resolution, not the screen's.
    const dpr = room && fit ? Math.min(3, Math.max(1, room.width / fit.w)) : 1;
    const ready = !!(room && fit && modelUrl && current && floorHelper);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
            <DialogContent
                className="flex h-[90vh] w-[95vw] max-w-[1280px] flex-col gap-3 p-4 sm:max-w-[1280px]"
                // A portal still bubbles through the React tree: without this a
                // drag inside the dialog would move the canvas node behind it.
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="pe-8">
                    <DialogTitle className="text-base font-medium">
                        {t('CONTENT_STUDIO.PLACE.TITLE', 'Place the product in the room')}
                    </DialogTitle>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        {t('CONTENT_STUDIO.PLACE.HINT', 'Drag the product across the floor. Turn and size it below, then validate.')}
                    </p>
                </div>

                <div ref={stageRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-muted">
                    {room && fit && (
                        <div className="relative" style={{ width: fit.w, height: fit.h }}>
                            <img
                                src={room.imageUrl}
                                alt=""
                                draggable={false}
                                className="absolute inset-0 size-full select-none object-fill"
                            />
                            {ready && (
                                <Canvas
                                    style={{ position: 'absolute', inset: 0 }}
                                    dpr={dpr}
                                    flat
                                    gl={{ alpha: true, preserveDrawingBuffer: true, antialias: true, stencil: true }}
                                    onCreated={({ gl }) => gl.setClearColor(0x000000, 0)}
                                    onPointerUp={endDrag}
                                    onPointerLeave={endDrag}
                                >
                                    {/* The room's own camera: at the origin, looking down -Z. */}
                                    <PerspectiveCamera makeDefault manual fov={room.fov} aspect={room.aspect} near={0.1} far={1000} position={[0, 0, 0]} />
                                    <CaptureBridge onReady={onThreeReady} />
                                    <ambientLight intensity={1.1} />
                                    <directionalLight position={[2, 6, 3]} intensity={1.4} />
                                    {room.occluder && (
                                        <mesh geometry={room.occluder} renderOrder={-1} frustumCulled={false}>
                                            <meshBasicMaterial colorWrite={false} depthWrite side={THREE.DoubleSide} />
                                        </mesh>
                                    )}
                                    <mesh
                                        position={floorHelper!.position}
                                        quaternion={floorHelper!.quaternion}
                                        userData={{ [HELPER]: true }}
                                        onPointerMove={onFloorMove}
                                        onPointerUp={endDrag}
                                    >
                                        <planeGeometry args={[40, 40]} />
                                        <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
                                    </mesh>
                                    <Suspense fallback={null}>
                                        <PlacedModel
                                            url={modelUrl!}
                                            isRug={isRug}
                                            largestDimensionM={largestDimensionM}
                                            placement={current!}
                                            floorNormal={room.floorNormal}
                                            onGrab={onGrab}
                                            groupRef={modelGroup}
                                        />
                                    </Suspense>
                                    <ControlsTracker target={modelGroup} overlay={controlsRef} />
                                </Canvas>
                            )}
                            {ready && current && (
                                <div
                                    ref={controlsRef}
                                    className="absolute flex items-center gap-1.5 rounded-full border border-foreground/10 bg-background/70 p-1 opacity-0 backdrop-blur-xl"
                                    style={{ transform: 'translate(-50%, calc(-100% - 10px))' }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                >
                                    <DragKnob
                                        value={current.rotDeg}
                                        sensitivity={0.8}
                                        onChange={setRotation}
                                        onReset={() => setRotation(0)}
                                        label={t('CONTENT_STUDIO.PLACE.DRAG_ROTATE', 'Drag to turn · double-click to reset') as string}
                                    >
                                        <RotateCw className="size-4" />
                                    </DragKnob>
                                    <DragKnob
                                        value={current.scale}
                                        sensitivity={0.004}
                                        onChange={setScale}
                                        onReset={() => setScale(1)}
                                        label={t('CONTENT_STUDIO.PLACE.DRAG_SIZE', 'Drag to resize · double-click to reset') as string}
                                    >
                                        <Scaling className="size-4" />
                                    </DragKnob>
                                    <span className="px-1.5 text-[11px] tabular-nums text-muted-foreground">
                                        {Math.round(current.rotDeg)}° · {Math.round(current.scale * 100)}%
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {!ready && !error && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="flex items-center gap-2 rounded-md bg-background/90 px-3 py-2 text-xs text-muted-foreground">
                                <Loader2 className="size-4 animate-spin" />
                                {!room
                                    ? t('CONTENT_STUDIO.PLACE.LOADING_ROOM', 'Loading the room…')
                                    : t('CONTENT_STUDIO.PLACE.LOADING_MODEL', 'Loading the 3D model…')}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="ms-auto flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setPlacement(null)} disabled={saving || !room}>
                            <RotateCcw className="size-3.5" />
                            {t('CONTENT_STUDIO.PLACE.RESET', 'Reset')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
                            {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                        </Button>
                        <Button size="sm" onClick={validate} disabled={saving || !ready}>
                            {saving && <Loader2 className="size-3.5 animate-spin" />}
                            {t('CONTENT_STUDIO.PLACE.VALIDATE', 'Validate placement')}
                        </Button>
                    </div>
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
            </DialogContent>
        </Dialog>
    );
};
